import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  LogOut,
  BookOpen,
  Loader2,
  Plus,
  Trash2,
  Eye,
  Send,
  Edit,
  Calendar,
  TrendingUp,
  FileText,
  BarChart3,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
  Sparkles,
  Copy,
  Check
} from 'lucide-react';

// ─── AI Prompt Content ───────────────────────────────────────────────────────
const AI_PROMPT_TEXT = `You are a senior Life Sciences Market Intelligence analyst writing the weekly "Market Pulse" report for Varyynt — a career platform for pharmaceutical, biotech, medical device, and digital health professionals. Your audience is mid-to-senior level life sciences candidates (Directors, VPs, Scientists, Medical Affairs leaders, Commercial executives) who are actively or passively exploring career opportunities.

Your job is to produce a complete, publication-ready Market Pulse report in the exact JSON structure defined below. The report should feel like a premium, data-backed briefing — authoritative, concise, and genuinely useful. Avoid filler and buzzwords.

---

## AUDIENCE CONTEXT
- Seniority: Director through C-Suite; also Senior Scientists and Clinical leaders
- Industries: Pharma (big and emerging), Biotech, MedTech, Digital Health, CROs
- Functions: Clinical Development, Medical Affairs, Commercial (Sales/Marketing), Regulatory, R&D, Data Science, Operations, Market Access, Finance
- Pain points: Opaque hiring markets, unclear salary benchmarks, uncertain job security
- What they want: Signal over noise — real data about where hiring is happening, which functions are growing, and what they should know this week

---

## CONTENT PHILOSOPHY

**Stat Cards** — Use for a single striking number that tells a story on its own.
Good: "68%" with label "of VP-level roles now require cross-functional experience"
Good: "$287K" with label "median base salary for CMO roles in biotech"
Bad: "5 things" or vague metrics without context

**Charts** — Use for distributions, rankings, or comparisons across 4–8 categories.
Good: Top therapeutic areas by open headcount
Good: Remote vs. Hybrid vs. Onsite breakdown by function
Bad: Charts with only 1–2 data points (use a stat card instead)

**Insights** — Use for narrative interpretation that data alone can't convey.
Good: A trend explanation, a "what this means for you" paragraph, a forward-looking call
Good: A contrarian observation (e.g., "Despite headlines, oncology hiring is slowing")
Bad: Restating the stat card numbers without adding interpretation

---

## TONE & STYLE
- Confident and direct. No "it appears that" or "it seems like."
- Specific over vague. Numbers beat adjectives.
- Candidate-first lens: always answer "what does this mean for my career?"
- Brief: intro_text max 3 sentences. Insight body max 4 sentences.
- Professional but not stiff. This is a Substack, not a press release.

---

## OUTPUT FORMAT

Respond ONLY with a valid JSON object using this exact structure. Do not add any text before or after the JSON.

{
  "title": "Market Pulse: Week of [Date]",
  "subtitle": "One-line framing of this week's theme (max 10 words)",
  "intro_text": "2–3 sentence opening that sets up why this week's data matters. Lead with the most interesting finding.",

  "stat_cards": [
    {
      "value": "Display value, e.g. 72% or $195K or 1,247",
      "label": "Short label explaining what this number represents (max 8 words)",
      "trend": "Optional: direction vs. last period, e.g. '+12% vs. last month'. Omit if no comparison data.",
      "color": "lime | blue | amber | purple"
    }
  ],

  "charts": [
    {
      "chart_type": "bar | horizontal_bar | pie",
      "title": "Chart title (concise, descriptive)",
      "data": [
        { "name": "Category label", "value": 45 },
        { "name": "Category label", "value": 32 }
      ]
    }
  ],

  "insights": [
    {
      "title": "Insight headline (5–7 words, punchy)",
      "body": "2–4 sentence paragraph. State the observation, explain why it's happening, and give candidates a practical implication.",
      "highlight": "Optional: single-sentence key takeaway under 20 words. Omit if nothing earns it."
    }
  ],

  "footer_text": "1–2 sentences citing data source and date.",
  "cta_label": "Button text, e.g. 'View Your Matches'",
  "cta_url": "/matches"
}

---

## STRUCTURAL GUIDELINES

Stat Cards: ideal 3–4, max 6
- lime = growth/positive signal
- blue = neutral/informational
- amber = caution/watch signal
- purple = executive/premium angle
- Value format: always include unit — "72%" not "72", "$195K" not "195000"

Charts: ideal 1–2, max 3
- "bar" — time series or ordered comparisons
- "horizontal_bar" — ranked lists (best when category names > 10 chars)
- "pie" — part-of-whole (max 6 slices)
- data values: whole integers only; min 3 entries, max 8 entries

Insights: ideal 2–3
- One "what's growing", one "what to watch", optional "salary signal"
- highlight: one screenshot-worthy sentence under 20 words, or omit

---

## EXAMPLE OUTPUT

{
  "title": "Market Pulse: Week of March 17, 2025",
  "subtitle": "Oncology cools; Commercial roles surge across mid-size biotech",
  "intro_text": "Hiring in large-cap oncology slowed for the third consecutive week, while commercial functions — particularly Market Access and MSLs — posted their strongest week of Q1. Mid-size biotechs drove 61% of new postings, a notable shift from the enterprise-dominated pattern of Q4.",
  "stat_cards": [
    { "value": "61%", "label": "of new roles posted by mid-size biotechs", "trend": "+18% vs. Q4 2024 average", "color": "lime" },
    { "value": "$248K", "label": "median base for VP Market Access", "trend": null, "color": "purple" },
    { "value": "3.1x", "label": "more MSL roles vs. this time last year", "trend": "↑ accelerating", "color": "blue" },
    { "value": "42%", "label": "of Director+ roles require regulatory experience", "trend": "+9% from 2023", "color": "amber" }
  ],
  "charts": [
    {
      "chart_type": "horizontal_bar",
      "title": "Top Therapeutic Areas by New Postings (This Week)",
      "data": [
        { "name": "Oncology", "value": 312 },
        { "name": "Neurology", "value": 198 },
        { "name": "Immunology", "value": 176 },
        { "name": "Rare Disease", "value": 143 },
        { "name": "Cardiology", "value": 89 }
      ]
    }
  ],
  "insights": [
    {
      "title": "MSL demand is outpacing supply",
      "body": "MSL postings are up 3x year-over-year, but the candidate pool with both scientific depth and field experience remains narrow. Companies are increasingly accepting candidates from adjacent roles. If you have an MD, PharmD, or relevant PhD with any field-facing experience, this is your window.",
      "highlight": "MSLs with rare disease backgrounds are receiving 2–3 competing offers within 30 days."
    }
  ],
  "footer_text": "Data reflects Varyynt's active database of 10,700+ life sciences roles as of March 17, 2025.",
  "cta_label": "View Your Job Matches",
  "cta_url": "/matches"
}

---

## TIPS FOR BEST RESULTS

Give the AI real data: "We have 312 new oncology postings this week, up from 267 last week"
Specify a theme: "Focus on remote work trends" or "Lead with the salary data"
Control length: "3 stat cards, 1 chart, 2 insights" or "Full report — 4 stat cards, 2 charts, 3 insights"

After the AI produces JSON, paste field-by-field into Admin → Market Pulse → Create Report.`;

// AI Prompt Modal
const AIPromptModal = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = AI_PROMPT_TEXT;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-lime-600" />
            AI Report Generation Prompt
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Copy this prompt and paste it into ChatGPT, Claude, or any AI assistant.
            Then follow with your brief, e.g. <em>"Write a Market Pulse for the week of [DATE] focused on [THEME]."</em>
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4 my-2">
          <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed">
            {AI_PROMPT_TEXT}
          </pre>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-3 pt-2">
          <p className="text-xs text-slate-400">
            Prompt saved at <code className="bg-slate-100 px-1 rounded">/app/memory/market_pulse_ai_prompt.md</code>
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            <Button
              size="sm"
              onClick={handleCopy}
              data-testid="copy-ai-prompt"
              className={copied ? 'bg-lime-600 hover:bg-lime-600 text-white' : 'bg-evergreen-950 hover:bg-evergreen-900 text-white'}
            >
              {copied ? (
                <><Check className="h-4 w-4 mr-1.5" />Copied!</>
              ) : (
                <><Copy className="h-4 w-4 mr-1.5" />Copy Prompt</>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
import { toast } from 'sonner';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

// Color options for stat cards
const STAT_COLORS = [
  { value: 'lime', label: 'Lime', class: 'bg-lime-100 text-lime-700 border-lime-200' },
  { value: 'blue', label: 'Blue', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'amber', label: 'Amber', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'purple', label: 'Purple', class: 'bg-purple-100 text-purple-700 border-purple-200' },
];

// Chart types
const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'horizontal_bar', label: 'Horizontal Bar' },
  { value: 'pie', label: 'Pie Chart' },
];

// Stat Card Editor
const StatCardEditor = ({ stat, index, onUpdate, onRemove }) => {
  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Stat Card #{index + 1}</span>
        <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Value (e.g., 87%, $195K)</Label>
          <Input 
            value={stat.value} 
            onChange={(e) => onUpdate(index, { ...stat, value: e.target.value })}
            placeholder="87%"
          />
        </div>
        <div>
          <Label className="text-xs">Label</Label>
          <Input 
            value={stat.label} 
            onChange={(e) => onUpdate(index, { ...stat, label: e.target.value })}
            placeholder="prefer remote work"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Trend (optional)</Label>
          <Input 
            value={stat.trend || ''} 
            onChange={(e) => onUpdate(index, { ...stat, trend: e.target.value })}
            placeholder="+5% from last month"
          />
        </div>
        <div>
          <Label className="text-xs">Color</Label>
          <Select value={stat.color} onValueChange={(val) => onUpdate(index, { ...stat, color: val })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAT_COLORS.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

// Chart Editor
const ChartEditor = ({ chart, index, onUpdate, onRemove }) => {
  const [chartDataText, setChartDataText] = useState(
    chart.data?.map(d => `${d.name}: ${d.value}`).join('\n') || ''
  );

  const parseChartData = (text) => {
    return text.split('\n')
      .filter(line => line.trim())
      .map(line => {
        const [name, value] = line.split(':').map(s => s.trim());
        return { name: name || '', value: parseInt(value) || 0 };
      });
  };

  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Chart #{index + 1}</span>
        <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Chart Title</Label>
          <Input 
            value={chart.title} 
            onChange={(e) => onUpdate(index, { ...chart, title: e.target.value })}
            placeholder="Top Functions"
          />
        </div>
        <div>
          <Label className="text-xs">Chart Type</Label>
          <Select value={chart.chart_type} onValueChange={(val) => onUpdate(index, { ...chart, chart_type: val })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CHART_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Chart Data (one per line: Name: Value)</Label>
        <Textarea 
          value={chartDataText} 
          onChange={(e) => {
            setChartDataText(e.target.value);
            onUpdate(index, { ...chart, data: parseChartData(e.target.value) });
          }}
          placeholder="Oncology: 45&#10;Neurology: 32&#10;Cardiology: 28"
          rows={4}
          className="font-mono text-sm"
        />
      </div>
    </div>
  );
};

// Insight Editor
const InsightEditor = ({ insight, index, onUpdate, onRemove }) => {
  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Insight #{index + 1}</span>
        <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
      <div>
        <Label className="text-xs">Title</Label>
        <Input 
          value={insight.title} 
          onChange={(e) => onUpdate(index, { ...insight, title: e.target.value })}
          placeholder="Key Insight"
        />
      </div>
      <div>
        <Label className="text-xs">Body</Label>
        <Textarea 
          value={insight.body} 
          onChange={(e) => onUpdate(index, { ...insight, body: e.target.value })}
          placeholder="Describe the insight..."
          rows={3}
        />
      </div>
      <div>
        <Label className="text-xs">Highlight/Callout (optional)</Label>
        <Input 
          value={insight.highlight || ''} 
          onChange={(e) => onUpdate(index, { ...insight, highlight: e.target.value })}
          placeholder="Key takeaway text"
        />
      </div>
    </div>
  );
};

// Create/Edit Modal
const MarketPulseEditorModal = ({ isOpen, onClose, onSave, editingPulse }) => {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [introText, setIntroText] = useState('');
  const [statCards, setStatCards] = useState([]);
  const [charts, setCharts] = useState([]);
  const [insights, setInsights] = useState([]);
  const [footerText, setFooterText] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [publishAt, setPublishAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    if (editingPulse) {
      setTitle(editingPulse.title || '');
      setSubtitle(editingPulse.subtitle || '');
      setIntroText(editingPulse.intro_text || '');
      setStatCards(editingPulse.stat_cards || []);
      setCharts(editingPulse.charts || []);
      setInsights(editingPulse.insights || []);
      setFooterText(editingPulse.footer_text || '');
      setCtaLabel(editingPulse.cta_label || '');
      setCtaUrl(editingPulse.cta_url || '');
      setPublishAt(editingPulse.publish_at ? format(parseISO(editingPulse.publish_at), "yyyy-MM-dd'T'HH:mm") : '');
      setExpiresAt(editingPulse.expires_at ? format(parseISO(editingPulse.expires_at), "yyyy-MM-dd'T'HH:mm") : '');
    } else {
      // Reset for new report
      setTitle(`Market Pulse: Week of ${format(new Date(), 'MMM d, yyyy')}`);
      setSubtitle('Key trends in life sciences hiring');
      setIntroText('');
      setStatCards([]);
      setCharts([]);
      setInsights([]);
      setFooterText('');
      setCtaLabel('');
      setCtaUrl('');
      setPublishAt('');
      setExpiresAt('');
    }
  }, [editingPulse, isOpen]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        intro_text: introText.trim() || null,
        stat_cards: statCards,
        charts: charts,
        insights: insights,
        footer_text: footerText.trim() || null,
        cta_label: ctaLabel.trim() || null,
        cta_url: ctaUrl.trim() || null,
        publish_at: publishAt ? new Date(publishAt).toISOString() : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      onClose();
      toast.success(editingPulse ? 'Report updated!' : 'Report created!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const addStatCard = () => {
    setStatCards([...statCards, { value: '', label: '', trend: '', color: 'lime' }]);
  };

  const addChart = () => {
    setCharts([...charts, { chart_type: 'bar', title: '', data: [] }]);
  };

  const addInsight = () => {
    setInsights([...insights, { title: '', body: '', highlight: '' }]);
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: FileText },
    { id: 'stats', label: 'Stat Cards', icon: TrendingUp, count: statCards.length },
    { id: 'charts', label: 'Charts', icon: BarChart3, count: charts.length },
    { id: 'insights', label: 'Insights', icon: Lightbulb, count: insights.length },
    { id: 'footer', label: 'Footer & CTA', icon: Globe },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-lime-600" />
            {editingPulse ? 'Edit Market Pulse Report' : 'Create Market Pulse Report'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Section Navigation */}
          <div className="w-48 border-r border-slate-200 pr-4 space-y-1">
            {sections.map(section => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === section.id
                      ? 'bg-lime-50 text-lime-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                  {section.count !== undefined && section.count > 0 && (
                    <Badge className="ml-auto" variant="secondary">{section.count}</Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Section Content */}
          <div className="flex-1 pl-6 overflow-y-auto max-h-[60vh]">
            {/* Basic Info */}
            {activeSection === 'basic' && (
              <div className="space-y-4">
                <div>
                  <Label>Report Title *</Label>
                  <Input 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Market Pulse: Week of Jan 15"
                  />
                </div>
                <div>
                  <Label>Subtitle</Label>
                  <Input 
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Key trends in life sciences hiring"
                  />
                </div>
                <div>
                  <Label>Introduction</Label>
                  <Textarea 
                    value={introText}
                    onChange={(e) => setIntroText(e.target.value)}
                    placeholder="Write an engaging introduction for this week's report..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Stat Cards */}
            {activeSection === 'stats' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">Add key statistics as visual cards</p>
                  <Button variant="outline" size="sm" onClick={addStatCard}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Stat Card
                  </Button>
                </div>
                {statCards.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    <TrendingUp className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No stat cards yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {statCards.map((stat, idx) => (
                      <StatCardEditor
                        key={idx}
                        stat={stat}
                        index={idx}
                        onUpdate={(i, newStat) => {
                          const updated = [...statCards];
                          updated[i] = newStat;
                          setStatCards(updated);
                        }}
                        onRemove={(i) => setStatCards(statCards.filter((_, j) => j !== i))}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Charts */}
            {activeSection === 'charts' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">Add data visualizations</p>
                  <Button variant="outline" size="sm" onClick={addChart}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Chart
                  </Button>
                </div>
                {charts.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    <BarChart3 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No charts yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {charts.map((chart, idx) => (
                      <ChartEditor
                        key={idx}
                        chart={chart}
                        index={idx}
                        onUpdate={(i, newChart) => {
                          const updated = [...charts];
                          updated[i] = newChart;
                          setCharts(updated);
                        }}
                        onRemove={(i) => setCharts(charts.filter((_, j) => j !== i))}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Insights */}
            {activeSection === 'insights' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">Add written insights and analysis</p>
                  <Button variant="outline" size="sm" onClick={addInsight}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Insight
                  </Button>
                </div>
                {insights.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    <Lightbulb className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No insights yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {insights.map((insight, idx) => (
                      <InsightEditor
                        key={idx}
                        insight={insight}
                        index={idx}
                        onUpdate={(i, newInsight) => {
                          const updated = [...insights];
                          updated[i] = newInsight;
                          setInsights(updated);
                        }}
                        onRemove={(i) => setInsights(insights.filter((_, j) => j !== i))}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Footer & CTA */}
            {activeSection === 'footer' && (
              <div className="space-y-4">
                <div>
                  <Label>Footer Text</Label>
                  <Textarea 
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    placeholder="Closing remarks or call-to-action text..."
                    rows={3}
                  />
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Label className="mb-2 block">Call-to-Action Button (optional)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Button Label</Label>
                      <Input 
                        value={ctaLabel}
                        onChange={(e) => setCtaLabel(e.target.value)}
                        placeholder="Update Your Profile"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Button URL</Label>
                      <Input 
                        value={ctaUrl}
                        onChange={(e) => setCtaUrl(e.target.value)}
                        placeholder="/profile"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule */}
            {activeSection === 'schedule' && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <strong>Schedule Options:</strong> Leave empty to save as draft, or set dates to schedule automatic publish/expiry.
                  </p>
                </div>
                <div>
                  <Label>Publish At (optional)</Label>
                  <Input 
                    type="datetime-local"
                    value={publishAt}
                    onChange={(e) => setPublishAt(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">When should this report become visible to candidates?</p>
                </div>
                <div>
                  <Label>Expires At (optional)</Label>
                  <Input 
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">When should this report be automatically hidden?</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="bg-evergreen-950 hover:bg-evergreen-900"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {editingPulse ? 'Update Report' : 'Create Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Preview Modal
const PreviewModal = ({ pulse, isOpen, onClose }) => {
  if (!pulse) return null;

  const getColorClass = (color) => {
    const colors = {
      lime: 'bg-lime-100 text-lime-700 border-lime-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      amber: 'bg-amber-100 text-amber-700 border-amber-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
    };
    return colors[color] || colors.lime;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-lime-600" />
            Preview Report
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-heading text-slate-900">{pulse.title}</h1>
            {pulse.subtitle && (
              <p className="text-slate-500 mt-1">{pulse.subtitle}</p>
            )}
          </div>

          {/* Intro */}
          {pulse.intro_text && (
            <p className="text-slate-600 mb-8">{pulse.intro_text}</p>
          )}

          {/* Stat Cards */}
          {pulse.stat_cards?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {pulse.stat_cards.map((stat, idx) => (
                <div key={idx} className={`p-4 rounded-xl border ${getColorClass(stat.color)}`}>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm">{stat.label}</p>
                  {stat.trend && <p className="text-xs opacity-75 mt-1">{stat.trend}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Charts */}
          {pulse.charts?.length > 0 && (
            <div className="space-y-6 mb-8">
              {pulse.charts.map((chart, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-medium text-slate-900 mb-3">{chart.title}</h3>
                  <div className="space-y-2">
                    {chart.data?.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-sm text-slate-600 w-32">{item.name}</span>
                        <div className="flex-1 h-6 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-lime-500 rounded-full"
                            style={{ width: `${Math.min(100, (item.value / Math.max(...chart.data.map(d => d.value))) * 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-900 w-12 text-right">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Insights */}
          {pulse.insights?.length > 0 && (
            <div className="space-y-4 mb-8">
              {pulse.insights.map((insight, idx) => (
                <div key={idx} className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-medium text-amber-900 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    {insight.title}
                  </h3>
                  <p className="text-amber-800 mt-2">{insight.body}</p>
                  {insight.highlight && (
                    <p className="text-sm font-medium text-amber-700 mt-2 italic">&ldquo;{insight.highlight}&rdquo;</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          {pulse.footer_text && (
            <p className="text-slate-600 text-center mb-4">{pulse.footer_text}</p>
          )}

          {/* CTA */}
          {pulse.cta_label && pulse.cta_url && (
            <div className="text-center">
              <Button className="bg-evergreen-950 hover:bg-evergreen-900">
                {pulse.cta_label}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminMarketPulsePage() {
  const { authAxios, logout } = useAuth();
  const [pulses, setPulses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPulse, setEditingPulse] = useState(null);
  const [previewPulse, setPreviewPulse] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);

  const loadPulses = useCallback(async () => {
    try {
      const response = await authAxios.get('/admin/market-pulse', {
        params: { limit: 50 }
      });
      setPulses(response.data.pulses || []);
    } catch (error) {
      console.error('Failed to load market pulses:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadPulses();
  }, [loadPulses]);

  const handleSave = async (data) => {
    if (editingPulse) {
      await authAxios.put(`/admin/market-pulse/${editingPulse.id}`, data);
    } else {
      await authAxios.post('/admin/market-pulse', data);
    }
    loadPulses();
  };

  const handlePublish = async (pulseId) => {
    try {
      await authAxios.put(`/admin/market-pulse/${pulseId}/publish`);
      loadPulses();
      toast.success('Report published!');
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish report');
    }
  };

  const handleDelete = async (pulseId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await authAxios.delete(`/admin/market-pulse/${pulseId}`);
      loadPulses();
      toast.success('Report deleted');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete report');
    }
  };

  const openEditor = (pulse = null) => {
    setEditingPulse(pulse);
    setEditorOpen(true);
  };

  const openPreview = async (pulseId) => {
    try {
      const response = await authAxios.get(`/admin/market-pulse/${pulseId}`);
      setPreviewPulse(response.data);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load preview');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading text-slate-900 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-lime-600" />
              Market Pulse
            </h1>
            <p className="text-slate-500 mt-1">Create and manage weekly market insight reports for candidates</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPromptModalOpen(true)}
              data-testid="open-ai-prompt-btn"
              className="border-lime-300 text-lime-700 hover:bg-lime-50"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              AI Prompt
            </Button>
            <Button onClick={() => openEditor()} className="bg-evergreen-950 hover:bg-evergreen-900">
              <Plus className="h-4 w-4 mr-2" />
              New Report
            </Button>
          </div>
        </div>

        {/* Reports List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Reports</CardTitle>
          </CardHeader>
          <CardContent>
            {pulses.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No reports yet</h3>
                <p className="text-slate-500 mb-4">Create your first weekly market pulse report</p>
                <Button onClick={() => openEditor()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Report
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {pulses.map(pulse => (
                  <div 
                    key={pulse.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${pulse.is_published ? 'bg-lime-100' : 'bg-slate-200'}`}>
                        <TrendingUp className={`h-5 w-5 ${pulse.is_published ? 'text-lime-600' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {pulse.is_published ? (
                            <Badge className="bg-lime-100 text-lime-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Published
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700">
                              <Clock className="h-3 w-3 mr-1" />
                              Draft
                            </Badge>
                          )}
                          {pulse.expires_at && new Date(pulse.expires_at) < new Date() && (
                            <Badge className="bg-red-100 text-red-700">
                              <XCircle className="h-3 w-3 mr-1" />
                              Expired
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-slate-900">{pulse.title}</h3>
                        {pulse.subtitle && (
                          <p className="text-sm text-slate-500">{pulse.subtitle}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">{pulse.stat_cards?.length || 0}</p>
                        <p className="text-xs text-slate-400">Stats</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">{pulse.charts?.length || 0}</p>
                        <p className="text-xs text-slate-400">Charts</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">{pulse.insights?.length || 0}</p>
                        <p className="text-xs text-slate-400">Insights</p>
                      </div>
                      <div className="text-right min-w-[100px]">
                        <p className="text-slate-600">{formatDistanceToNow(new Date(pulse.created_at), { addSuffix: true })}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openPreview(pulse.id)} data-testid={`preview-pulse-${pulse.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditor(pulse)} data-testid={`edit-pulse-${pulse.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {!pulse.is_published && (
                          <Button variant="ghost" size="sm" onClick={() => handlePublish(pulse.id)} data-testid={`publish-pulse-${pulse.id}`}>
                            <Send className="h-4 w-4 text-lime-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(pulse.id)} data-testid={`delete-pulse-${pulse.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Editor Modal */}
      <MarketPulseEditorModal
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingPulse(null);
        }}
        onSave={handleSave}
        editingPulse={editingPulse}
      />

      {/* Preview Modal */}
      <PreviewModal
        pulse={previewPulse}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      {/* AI Prompt Modal */}
      <AIPromptModal
        isOpen={promptModalOpen}
        onClose={() => setPromptModalOpen(false)}
      />
    </div>
  );
}
